import {useRef} from 'react';
import {
	defaultRangeExtractor,
	type Range,
	type Virtualizer,
} from '@tanstack/react-virtual';
import {type InternalContextDescriptor} from '@netx/core-components/ArrowFocusTrap';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';

export type UseArrowFocusTrapVirtualUtilsParams = {
	rowVirtualizerRef: React.RefObject<Virtualizer<any, Element>>;
	arrowFocusTrapRef: React.RefObject<InternalContextDescriptor>;
	totalItems: number;
};

export const useArrowFocusTrapVirtualUtils = ({
	arrowFocusTrapRef,
	rowVirtualizerRef,
	totalItems,
}: UseArrowFocusTrapVirtualUtilsParams) => {
	const lastActiveIndex = useRef(-1);

	const rangeExtractor = useConstCallback((range: Range) => {
		// handle the virtualizer range change and keep the arrow focus trap in sync
		if (lastActiveIndex.current < range.startIndex) {
			arrowFocusTrapRef.current?.setActiveIndex(
				rowVirtualizerRef.current?.range?.startIndex || 0,
			);
		} else if (lastActiveIndex.current > range.endIndex) {
			arrowFocusTrapRef.current?.setActiveIndex(
				(rowVirtualizerRef.current?.range?.endIndex || 1) - 1,
			);
		}
		return defaultRangeExtractor(range);
	});

	const handleOnActiveIndexChange = useConstCallback((index: number) => {
		const direction = lastActiveIndex.current > index ? 0 : 1;
		if (index < 0 || index > totalItems) return;
		rowVirtualizerRef.current?.scrollToIndex(
			Math.min(index + direction, totalItems - 1),
			{
				behavior: 'auto',
				align: index === totalItems - 1 ? 'start' : 'auto',
			},
		);
		lastActiveIndex.current = index;
	});

	return {
		lastActiveIndex,
		handleOnActiveIndexChange,
		rangeExtractor,
	};
};
